'use client';

import React, { useMemo, type ReactNode } from 'react';
import { SettingsProvider as CoreSettingsProvider, type SettingsBlob, type SettingsStore } from '@puzzlio/core';
import { SettingsAPI } from '@/app/lib/settings-api';

/*
  The web's storage for the shared settings provider.

  The provider itself lives in core so the app and the website behave identically; this supplies the
  browser half. `SettingsAPI` stays exactly as it is — several places read a setting synchronously,
  outside React (the games' PlayStateProviders), and those depend on localStorage being
  synchronous. Only the React-facing provider is shared.
*/

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const store = useMemo<SettingsStore>(
    () => ({
      load: () => SettingsAPI.getAllSettings(),
      save: (settings: SettingsBlob) => SettingsAPI.saveAllSettings(settings),
      // Another tab writing settings, and this tab's own writes through the static API.
      subscribe: (onChange) => {
        const onStorage = (event: StorageEvent) => {
          if (event.key === 'puzzlio_settings') onChange();
        };
        window.addEventListener('storage', onStorage);
        window.addEventListener('settingsChanged', onChange);
        return () => {
          window.removeEventListener('storage', onStorage);
          window.removeEventListener('settingsChanged', onChange);
        };
      },
    }),
    []
  );

  return <CoreSettingsProvider store={store}>{children}</CoreSettingsProvider>;
};
